import React, { useState, useMemo, useEffect } from "react";
import { MaterialReactTable } from "material-react-table";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Chip,
    CircularProgress,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup,
    InputAdornment,
} from "@mui/material";
import { Edit, Delete, Add, Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import "./UserManagement.css";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const DISABILITY_TYPES = ['Seeing', 'Listening', 'Walking', 'Speaking', 'Others'];

// The primary/founding Super Admin account - locked here in the UI (role
// can't be changed, account can't be deleted, by anyone) so the system
// can never be left without one. Frontend-only - not enforced by the API.
const PROTECTED_ADMIN_EMAILS = new Set(["dnmgkoko@gmail.com"]);
const isProtectedUser = (user) => !!user && PROTECTED_ADMIN_EMAILS.has(user.email);

// A small rotating palette so any number of dynamic roles (including custom
// ones a Super Admin creates) get a distinct-looking chip color, without
// needing to hardcode one per role.
const ROLE_COLOR_PALETTE = ["#7c3aed", "#2563eb", "#0891b2", "#059669", "#d97706", "#dc2626", "#64748b", "#9333ea", "#0d9488"];

function authHeaders() {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const UserManagement = () => {
    const currentUserId = parseInt(localStorage.getItem("user_id"), 10);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]); // [{code, name, ...}] fetched from /login/roles/
    const [, setFetchLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editForm, setEditForm] = useState({ full_name: "", email: "", role: "" });
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        full_name: "", gender: "", age_category: "", position: "",
        contact_number: "", has_disability: "", disability_types: [],
        email: "", password: "", role: "",
    });
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState("");
    const [addShowPassword, setAddShowPassword] = useState(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setFetchLoading(true);
            try {
                const response = await fetch(`${backendUrl}login/users/`, { headers: authHeaders() });
                const data = await response.json();
                if (response.ok) setUsers(Array.isArray(data) ? data : data.results ?? []);
            } catch (err) {
                console.error("Failed to fetch users:", err);
            } finally {
                setFetchLoading(false);
            }
        };
        const fetchRoles = async () => {
            try {
                const response = await fetch(`${backendUrl}login/roles/`, { headers: authHeaders() });
                const data = await response.json();
                if (response.ok) setRoles(Array.isArray(data) ? data : data.results ?? []);
            } catch (err) {
                console.error("Failed to fetch roles:", err);
            }
        };
        fetchUsers();
        fetchRoles();
    }, []);

    // Dynamic role lookups derived from whatever roles currently exist in the
    // database (built-in + any custom ones a Super Admin has created), so
    // this screen never needs a code change when roles change.
    const ROLE_OPTIONS = useMemo(
        () => roles.map((r) => ({ value: r.code, label: r.name })),
        [roles]
    );
    const ROLE_LABELS = useMemo(
        () => Object.fromEntries(roles.map((r) => [r.code, r.name])),
        [roles]
    );
    const ROLE_COLORS = useMemo(
        () => Object.fromEntries(roles.map((r, idx) => [r.code, ROLE_COLOR_PALETTE[idx % ROLE_COLOR_PALETTE.length]])),
        [roles]
    );

    // --- Edit ---
    const handleEditOpen = (user) => {
        setSelectedUser(user);
        setEditForm({
            full_name: user.full_name || '',
            email: user.email || '',
            gender: user.gender || '',
            age_category: user.age_category || '',
            position: user.position || '',
            contact_number: user.contact_number || '',
            has_disability: user.has_disability || '',
            disability_types: user.disability_types || '',
            role: user.role || '',
            date_joined: user.date_joined || '',
        });
        setEditError("");
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        setEditError("");
        if (!editForm.role) { setEditError("Role is required."); return; }
        setEditLoading(true);
        try {
            const response = await fetch(`${backendUrl}login/users/${selectedUser.id}/update/`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ role: editForm.role }),
            });
            const data = await response.json();
            if (response.ok) {
                setUsers((prev) =>
                    prev.map((u) => (u.id === selectedUser.id ? { ...u, role: editForm.role } : u))
                );
                setEditDialogOpen(false);
                setSelectedUser(null);
            } else {
                const errorMsg = data.role || data.detail || "Failed to update user. Please try again.";
                setEditError(errorMsg);
            }
        } catch (err) {
            setEditError(`Failed to connect to the server: ${err.message}`);
        } finally {
            setEditLoading(false);
        }
    };

    // --- Delete ---
    const handleDeleteOpen = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            const response = await fetch(`${backendUrl}login/users/${selectedUser.id}/delete/`, {
                method: "DELETE",
                headers: authHeaders(),
            });
            if (response.ok) {
                setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
                setDeleteDialogOpen(false);
                setSelectedUser(null);
            } else {
                const data = await response.json().catch(() => ({}));
                alert(data.detail || "Failed to delete user. Please try again.");
            }
        } catch (err) {
            alert(`Failed to connect to the server: ${err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    // --- Add ---
    const handleAddOpen = () => {
        setAddForm({
            full_name: "", gender: "", age_category: "", position: "",
            contact_number: "", has_disability: "", disability_types: [],
            email: "", password: "", role: "",
        });
        setAddError("");
        setAddShowPassword(false);
        setAddDialogOpen(true);
    };

    const handleDisabilityTypeChange = (type) => {
        setAddForm((prev) => ({
            ...prev,
            disability_types: prev.disability_types.includes(type)
                ? prev.disability_types.filter((t) => t !== type)
                : [...prev.disability_types, type],
        }));
    };

    const handleAddSave = async () => {
        setAddError("");
        if (!addForm.full_name || addForm.full_name.length < 3) { setAddError("Full name is required (min 3 chars)."); return; }
        if (!addForm.gender) { setAddError("Gender is required."); return; }
        if (!addForm.age_category) { setAddError("Age category is required."); return; }
        if (!addForm.position) { setAddError("Position is required."); return; }
        if (!addForm.contact_number) { setAddError("Contact number is required."); return; }
        if (!addForm.has_disability) { setAddError("Please specify if the user has disabilities."); return; }
        if (addForm.has_disability === "yes" && addForm.disability_types.length === 0) { setAddError("Please select at least one disability type."); return; }
        if (!addForm.email) { setAddError("Email is required."); return; }
        if (!addForm.password) { setAddError("Password is required."); return; }
        if (!addForm.role) { setAddError("Role is required."); return; }

        setAddLoading(true);
        try {
            // Admin-created users go through /login/users/create/, not the
            // public /login/register/ flow — that one always forces role='user'
            // and ignores any role passed in, by design, so it can't be used
            // to create an Admin account.
            const response = await fetch(`${backendUrl}login/users/create/`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    full_name: addForm.full_name,
                    gender: addForm.gender,
                    age_category: addForm.age_category,
                    position: addForm.position,
                    contact_number: addForm.contact_number,
                    has_disability: addForm.has_disability,
                    disability_types: addForm.has_disability === "yes" ? addForm.disability_types : [],
                    email: addForm.email,
                    password: addForm.password,
                    role: addForm.role,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setUsers((prev) => [data, ...prev]);
                setAddDialogOpen(false);
            } else {
                const rawError = data.email || data.password || data.role || data.detail || "Failed to create user. Please try again.";
                setAddError(Array.isArray(rawError) ? rawError.join(" ") : rawError);
            }
        } catch (err) {
            setAddError(`Failed to connect to the server: ${err.message}`);
        } finally {
            setAddLoading(false);
        }
    };

    // --- Columns ---
    const columns = useMemo(
        () => [
            {
                accessorKey: "full_name",
                header: "Name",
                size: 180,
                Cell: ({ row, cell }) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>{cell.getValue()}</span>
                        {isProtectedUser(row.original) && (
                            <Tooltip title="Primary Super Admin — role and account are locked" arrow>
                                <Lock sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </Tooltip>
                        )}
                    </Box>
                ),
            },
            {
                accessorKey: "email",
                header: "Email",
                size: 220,
                Cell: ({ cell }) => (
                    <span style={{ color: "#475569" }}>{cell.getValue()}</span>
                ),
            },
            {
                accessorKey: "date_joined",
                header: "Created Date",
                size: 150,
                Cell: ({ cell }) => (
                    <span style={{ color: "#64748b", fontSize: "13px" }}>
                        {formatDate(cell.getValue())}
                    </span>
                ),
            },
            {
                accessorKey: "role",
                header: "Role",
                size: 140,
                Cell: ({ cell }) => {
                    const role = cell.getValue();
                    return (
                        <Chip
                            label={ROLE_LABELS[role] || role}
                            size="small"
                            sx={{
                                backgroundColor: `${ROLE_COLORS[role] || "#64748b"}18`,
                                color: ROLE_COLORS[role] || "#64748b",
                                fontWeight: 600,
                                fontSize: "12px",
                                border: `1px solid ${ROLE_COLORS[role] || "#64748b"}30`,
                            }}
                        />
                    );
                },
                filterVariant: "select",
                filterSelectOptions: ROLE_OPTIONS.map((r) => ({ text: r.label, value: r.value })),
            },

        ],
        [ROLE_LABELS, ROLE_COLORS, ROLE_OPTIONS]
    );

    return (
        <div className="user-management-container">
            {/* Header */}
            <div className="user-management-header">
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddOpen}
                    sx={{
                        backgroundColor: "#2563eb",
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        px: 2.5,
                        py: 1,
                        fontSize: "13px",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                        "&:hover": { backgroundColor: "#1d4ed8", boxShadow: "0 4px 12px rgba(37, 99, 235, 0.35)" },
                    }}
                >
                    Add New User
                </Button>
                <Chip
                    label={`${users.length} Users`}
                    sx={{
                        backgroundColor: "#f1f5f9",
                        color: "#475569",
                        fontWeight: 600,
                        fontSize: "13px",
                        border: "1px solid #e2e8f0",
                    }}
                />
            </div>

            {/* Table */}
            <Box sx={{ mt: 2 }}>
                <MaterialReactTable
                    columns={columns}
                    data={users}
                    enableColumnFilters
                    enableColumnActions={false}
                    enableSorting
                    enablePagination
                    enableDensityToggle={false}
                    enableFullScreenToggle={false}
                    enableRowActions
                    positionActionsColumn="last"
                    initialState={{
                        density: "comfortable",
                        pagination: { pageSize: 10, pageIndex: 0 },
                        sorting: [{ id: "date_joined", desc: true }],
                    }}
                    renderRowActions={({ row }) => {
                        const isCurrentUser = row.original.id === currentUserId;
                        if (isProtectedUser(row.original)) {
                            return (
                                <Tooltip title="Primary Super Admin — locked, no edit or delete" arrow>
                                    <Lock fontSize="small" sx={{ color: "#94a3b8" }} />
                                </Tooltip>
                            );
                        }
                        return (
                            <Box sx={{ display: "flex", gap: "4px" }}>
                                <Tooltip title="Edit User" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEditOpen(row.original)}
                                        sx={{ color: "#2563eb", "&:hover": { backgroundColor: "#2563eb14" } }}
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {!isCurrentUser && (
                                    <Tooltip title="Delete User" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteOpen(row.original)}
                                            sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#ef444414" } }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    }}
                    muiTableContainerProps={{
                        sx: { maxHeight: "calc(100vh - 260px)", borderRadius: "12px" },
                    }}
                    muiTableHeadCellProps={{
                        sx: {
                            fontWeight: 700,
                            backgroundColor: "#f8fafc",
                            color: "#475569",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: "2px solid #e2e8f0",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        },
                    }}
                    muiTableBodyCellProps={{
                        sx: {
                            fontSize: "13px",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            borderBottom: "1px solid #f1f5f9",
                        },
                    }}
                    muiTableBodyRowProps={({ row }) => ({
                        sx: {
                            backgroundColor: row.original.id === currentUserId ? "#eff6ff" : undefined,
                            "&:hover": { backgroundColor: row.original.id === currentUserId ? "#dbeafe" : "#f8fafc" },
                            transition: "background-color 0.15s ease",
                        },
                    })}
                    muiTopToolbarProps={{
                        sx: {
                            backgroundColor: "transparent",
                            "& .MuiTextField-root": { minWidth: "280px" },
                        },
                    }}
                    muiBottomToolbarProps={{
                        sx: { backgroundColor: "transparent", borderTop: "1px solid #e2e8f0" },
                    }}
                />
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => !editLoading && setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        p: 1,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#1e293b",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        pb: 1,
                    }}
                >
                    Edit User
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField label="Full Name" value={editForm.full_name} disabled fullWidth size="small" variant="outlined" />
                        <TextField label="Email" value={editForm.email} disabled fullWidth size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField label="Gender" value={editForm.gender || ''} disabled fullWidth size="small" variant="outlined" select>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>
                        <TextField label="Age Category" value={editForm.age_category || ''} disabled fullWidth size="small" variant="outlined" select>
                            <MenuItem value="0-18">0-18</MenuItem>
                            <MenuItem value="19-50">19-50</MenuItem>
                            <MenuItem value="51+">51+</MenuItem>
                        </TextField>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField label="Position" value={editForm.position || ''} disabled fullWidth size="small" variant="outlined" />
                        <TextField label="Contact Number" value={editForm.contact_number || ''} disabled fullWidth size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField label="Has Disability" value={editForm.has_disability || ''} disabled fullWidth size="small" variant="outlined" select>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </TextField>
                        <TextField label="Disability Types" value={editForm.disability_types || ''} disabled fullWidth size="small" variant="outlined" />
                    </Box>
                    <TextField label="Date Joined" value={editForm.date_joined || ''} disabled fullWidth size="small" variant="outlined" type="date" InputLabelProps={{ shrink: true }} />
                    <TextField
                        label="Role"
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        disabled={isProtectedUser(selectedUser)}
                        fullWidth size="small" variant="outlined" select
                    >
                        {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </TextField>
                    {isProtectedUser(selectedUser) && (
                        <Typography sx={{ color: "#64748b", fontSize: "12.5px" }}>
                            This is the primary Super Admin account — its role is locked and it cannot be deleted.
                        </Typography>
                    )}
                    {editError && (
                        <Typography sx={{ color: "#ef4444", fontSize: "13px" }}>{editError}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setEditDialogOpen(false)}
                        disabled={editLoading}
                        sx={{
                            color: "#64748b",
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        disabled={editLoading || isProtectedUser(selectedUser)}
                        sx={{
                            backgroundColor: "#2563eb",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 3,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            "&:hover": { backgroundColor: "#1d4ed8" },
                            minWidth: "120px",
                        }}
                    >
                        {editLoading ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        p: 1,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#1e293b",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        pb: 1,
                    }}
                >
                    Delete User
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            color: "#475569",
                            fontSize: "14px",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        }}
                    >
                        Are you sure you want to delete{" "}
                        <strong style={{ color: "#1e293b" }}>{selectedUser?.full_name}</strong>? This
                        action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleteLoading}
                        sx={{
                            color: "#64748b",
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        sx={{
                            backgroundColor: "#ef4444",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 3,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            "&:hover": { backgroundColor: "#dc2626" },
                            minWidth: "90px",
                        }}
                    >
                        {deleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add New User Dialog */}
            <Dialog
                open={addDialogOpen}
                onClose={() => !addLoading && setAddDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#1e293b",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        pb: 1,
                    }}
                >
                    Add New User
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>
                    <TextField
                        label="Full Name"
                        value={addForm.full_name}
                        onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
                        fullWidth size="small" variant="outlined"
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Gender"
                            value={addForm.gender}
                            onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}
                            fullWidth size="small" variant="outlined" select
                        >
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>
                        <TextField
                            label="Age Category"
                            value={addForm.age_category}
                            onChange={(e) => setAddForm({ ...addForm, age_category: e.target.value })}
                            fullWidth size="small" variant="outlined" select
                        >
                            <MenuItem value="below_18">Below 18</MenuItem>
                            <MenuItem value="19-50">19-50</MenuItem>
                            <MenuItem value="above_50">More than 50</MenuItem>
                        </TextField>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Position"
                            value={addForm.position}
                            onChange={(e) => setAddForm({ ...addForm, position: e.target.value })}
                            fullWidth size="small" variant="outlined"
                        />
                        <TextField
                            label="Contact Number"
                            value={addForm.contact_number}
                            onChange={(e) => setAddForm({ ...addForm, contact_number: e.target.value })}
                            fullWidth size="small" variant="outlined"
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
                            Do you consider this user as a person with disabilities?
                        </Typography>
                        <RadioGroup
                            row
                            value={addForm.has_disability}
                            onChange={(e) => setAddForm({ ...addForm, has_disability: e.target.value, disability_types: [] })}
                        >
                            <FormControlLabel value="yes" control={<Radio size="small" />} label={<Typography sx={{ fontSize: "13px" }}>Yes</Typography>} />
                            <FormControlLabel value="no" control={<Radio size="small" />} label={<Typography sx={{ fontSize: "13px" }}>No</Typography>} />
                        </RadioGroup>
                    </Box>
                    {addForm.has_disability === "yes" && (
                        <Box>
                            <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
                                Select the types of disabilities:
                            </Typography>
                            <FormGroup row>
                                {DISABILITY_TYPES.map((type) => (
                                    <FormControlLabel
                                        key={type}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={addForm.disability_types.includes(type)}
                                                onChange={() => handleDisabilityTypeChange(type)}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: "13px" }}>{type}</Typography>}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    )}
                    <TextField
                        label="Email"
                        value={addForm.email}
                        onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                        fullWidth size="small" variant="outlined" type="email"
                    />
                    <TextField
                        label="Password"
                        value={addForm.password}
                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                        fullWidth size="small" variant="outlined"
                        type={addShowPassword ? "text" : "password"}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setAddShowPassword((p) => !p)} edge="end">
                                        {addShowPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Role"
                        value={addForm.role}
                        onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                        fullWidth size="small" variant="outlined" select
                    >
                        {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </TextField>
                    {addError && (
                        <Typography sx={{ color: "#ef4444", fontSize: "13px" }}>{addError}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setAddDialogOpen(false)}
                        disabled={addLoading}
                        sx={{
                            color: "#64748b",
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddSave}
                        disabled={addLoading}
                        sx={{
                            backgroundColor: "#2563eb",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 3,
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            "&:hover": { backgroundColor: "#1d4ed8" },
                            minWidth: "100px",
                        }}
                    >
                        {addLoading ? <CircularProgress size={18} color="inherit" /> : "Add User"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UserManagement;
